import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal, Stagger, fadeItem } from "@/components/site/Reveal";
import { motion } from "framer-motion";
import { VERTICALS } from "@/lib/content";

const PILLARS = ["Nation Building", "Youth Leadership", "Thought Leadership"];

export default function Initiatives() {
  return (
    <main data-testid="initiatives-page" className="bg-parchment text-ocean">
      {/* HERO */}
      <section className="pt-40 pb-16 md:pt-48 md:pb-24 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-spice">Initiatives</div>
            <h1 className="font-serif-display text-6xl md:text-8xl mt-6 leading-[0.85]">
              Twelve ways
              <br />
              <em className="text-spice">we show up.</em>
            </h1>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-ocean/75 text-lg md:text-xl leading-relaxed">
              Yi Kozhikode runs its work through twelve verticals organised under three national pillars.
              Each one is a doorway — pick the one that pulls you in.
            </p>
          </div>
        </div>
      </section>

      {/* PILLAR INDEX */}
      <section className="py-12 border-b border-black/10 bg-parchment-deep">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p} delay={i}>
              <a href={`#${p.toLowerCase().replace(/ /g, '-')}`} className="block group">
                <div className="overline text-spice">Pillar 0{i + 1}</div>
                <div className="font-serif text-3xl md:text-4xl mt-2 group-hover:text-spice transition-colors">{p}</div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VERTICALS BY PILLAR */}
      {PILLARS.map((pillar) => {
        const filtered = VERTICALS.filter((v) => v.pillar === pillar);
        return (
          <section
            key={pillar}
            id={pillar.toLowerCase().replace(/ /g, '-')}
            className="py-20 md:py-32 border-b border-black/10"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-12 gap-12 mb-12">
                <div className="md:col-span-5">
                  <div className="overline text-spice">{pillar}</div>
                  <h2 className="font-serif-display text-4xl md:text-6xl mt-4">{pillar}.</h2>
                </div>
                <div className="md:col-span-6 md:col-start-7 self-end">
                  <p className="text-ocean/70 leading-relaxed">
                    {pillar === "Nation Building" &&
                      "Tangible action on the issues India can’t afford to ignore — safety, accessibility, climate, child protection."}
                    {pillar === "Youth Leadership" &&
                      "Building the next generation of leaders — from school assemblies (Thalir) to college chapters (Yuva)."}
                    {pillar === "Thought Leadership" &&
                      "A contributory movement of constructive action — entrepreneurship, innovation and collective voice."}
                  </p>
                </div>
              </div>

              <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {filtered.map((v) => (
                  <motion.article
                    key={v.slug}
                    variants={fadeItem}
                    data-testid={`initiative-card-${v.slug}`}
                    className="group bg-parchment p-8 md:p-10 hover:bg-parchment-deep transition-colors min-h-[280px] flex flex-col"
                  >
                    <div className="flex items-start justify-between">
                      <span className="overline text-spice">{v.tag}</span>
                      <span className="font-serif text-xl text-ocean/30">/{filtered.indexOf(v) + 1}</span>
                    </div>
                    <h3 className="font-serif text-4xl md:text-5xl mt-10">{v.name}</h3>
                    <p className="mt-4 text-ocean/70 leading-relaxed flex-1">{v.desc}</p>
                    <div className="overline text-ocean/40 mt-6 group-hover:text-spice transition-colors flex items-center gap-2">
                      Learn more <ArrowUpRight size={14} weight="bold" />
                    </div>
                  </motion.article>
                ))}
              </Stagger>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-24 md:py-36 bg-ocean text-parchment relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.9]">
              Pick a vertical.
              <br />
              <em className="text-brass">We’ll find your project.</em>
            </h2>
            <p className="mt-6 text-parchment/70 max-w-xl">
              Students, professionals, founders, retirees — every vertical needs hands. Tell us where
              you’d like to plug in.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="initiatives-cta-join"
              className="overline px-8 py-5 bg-brass text-ocean hover:bg-parchment transition-colors flex items-center gap-2"
            >
              Start the conversation <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
